import React from 'react';
import { FiServer,FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiShield, FiZap, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';

const ServerRoomObsolete = () => {
  const problems = [
    {
      icon: <FiServer className="w-6 h-6" />,
      title: 'Servers Are Expensive to Buy and Maintain',
      description: 'A server room requires significant capital investment. Servers, storage devices, networking equipment, UPS systems, cooling units—the costs add up quickly. And then there\'s the ongoing maintenance: hardware failures, replacements, upgrades, and repairs.',
      stat: '₹3-10 Lakhs+ annual cost for server room maintenance',
      solution: 'Cloud ERP eliminates all hardware costs. Pay only for what you use, with predictable subscription pricing.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'IT Staff Burnout and Turnover',
      description: 'Managing servers is thankless work. Server rooms require dedicated IT staff who handle night shifts, weekend emergencies, and constant maintenance. Burnout is high. Turnover is costly. And institutions struggle to find qualified staff.',
      stat: '70% of IT staff report burnout from server management',
      solution: 'Cloud ERP requires minimal IT staff. No hardware management, no night shifts, no weekend emergencies.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Security Vulnerabilities',
      description: 'Server rooms are security risks. Outdated hardware, unpatched vulnerabilities, physical security threats, and insufficient monitoring create significant exposure. Most institutions cannot afford the level of security that cloud providers offer.',
      stat: '60% of data breaches involve on-premise server vulnerabilities',
      solution: 'Cloud ERP includes enterprise-grade security—encryption, monitoring, and automatic patching.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'Cooling and Energy Costs',
      description: 'Servers generate enormous heat. Keeping them cool requires expensive air conditioning systems that run 24/7. Energy costs alone can rival the cost of the servers. And cooling failures can damage hardware and cause data loss.',
      stat: '₹1-2 Lakhs annual electricity and cooling costs',
      solution: 'Cloud providers handle cooling and energy at scale—efficiently and cost-effectively.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Scaling Is a Nightmare',
      description: 'Adding capacity means buying more hardware, finding space, managing power and cooling. This process takes weeks or months. And if you need to scale down, you\'re stuck with unused hardware that still requires maintenance.',
      stat: 'Adding server capacity takes 4-8 weeks for most institutions',
      solution: 'Cloud ERP scales instantly. Add capacity with a click. Scale down just as easily.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiDollarSign className="w-6 h-6" />,
      title: 'Obsolescence Is Inevitable',
      description: 'Hardware becomes obsolete in 3-5 years. When it does, you need to replace it—another capital expense, another migration, another headache. The cycle never ends. You\'re always spending money on hardware that\'s already outdated.',
      stat: 'Servers become obsolete every 3-5 years',
      solution: 'Cloud is always current. No hardware lifecycle. No replacement costs. Always up-to-date.',
      color: 'bg-blue-50'
    }
  ];

  const alternatives = [
    {
      title: 'Move to Cloud ERP',
      description: 'Eliminate the server room completely. Run your entire institution from the cloud. No hardware, no IT staff, no maintenance.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Adopt a SaaS Model',
      description: 'Use Software as a Service. Pay a subscription. No infrastructure to manage. Always up-to-date.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Focus on Strategic IT',
      description: 'Redefine IT from "server maintainers" to "strategic partners." Let your IT staff focus on innovation, not hardware management.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Use Cloud Backup',
      description: 'Start with cloud backup. Then gradually move applications to the cloud. A phased approach reduces risk.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Partner with Cloud Experts',
      description: 'Work with providers who specialize in educational cloud solutions. Get the benefits without the complexity.',
      icon: <FiUsers className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Walk into any institution's server room and you'll see the same thing: rows of blinking hardware, humming cooling systems, and racks of cables. It's impressive. It's expensive. And increasingly, it's unnecessary.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The server room was a necessity for decades. Institutions needed on-site hardware to run their ERP systems. But <strong className="text-slate-900">in 2026, the server room has become an expensive liability.</strong> It consumes budgets, requires specialized staff, creates security risks, and slows down innovation.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's why your institution's server room is obsolete—and what to do about it.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">₹3-10L+</div>
          <div className="text-sm text-slate-500">Annual server room cost</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">70%</div>
          <div className="text-sm text-slate-500">IT staff burnout from server management</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">3-5</div>
          <div className="text-sm text-slate-500">Years before hardware is obsolete</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Your Server Room Is Obsolete</h2>

      {/* Problems List */}
      {problems.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">⚠️ {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Cloud Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Alternatives Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What to Do Instead</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {alternatives.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Server Room vs. Cloud ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Server Room</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>High capital expenditure</span></li>
              <li className="flex items-start gap-2">• <span>Expensive ongoing maintenance</span></li>
              <li className="flex items-start gap-2">• <span>Dedicated IT staff required</span></li>
              <li className="flex items-start gap-2">• <span>Slow to scale</span></li>
              <li className="flex items-start gap-2">• <span>Becomes obsolete every 3-5 years</span></li>
              <li className="flex items-start gap-2">• <span>Access limited to campus</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Cloud ERP with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Zero upfront costs</span></li>
              <li className="flex items-start gap-2">• <span>Predictable subscription pricing</span></li>
              <li className="flex items-start gap-2">• <span>Minimal IT staff needed</span></li>
              <li className="flex items-start gap-2">• <span>Instant scaling</span></li>
              <li className="flex items-start gap-2">• <span>Always current, never obsolete</span></li>
              <li className="flex items-start gap-2">• <span>Accessible from anywhere</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The ROI */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Financial Case for Moving to Cloud</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">₹5-10 Lakhs</h4>
            <p className="text-xs text-slate-600">Annual savings on IT costs</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">100%</h4>
            <p className="text-xs text-slate-600">No hardware replacement costs</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">80%</h4>
            <p className="text-xs text-slate-600">Reduction in IT staff workload</p>
          </div>
        </div>
      </div>

      {/* The Transition Story */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">A Real Story</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          "We had a server room that was costing us ₹8 lakhs annually to maintain. The hardware was aging. Our IT staff was exhausted. We moved to cloud ERP and eliminated the entire server room. Now we spend a fraction of that, our staff focuses on innovation instead of maintenance, and teachers access the system from anywhere. It was the best decision we made."
        </p>
        <p className="text-amber-800 leading-relaxed">
          — Director, Educational Trust, Pune
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Makes the Server Room Obsolete</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">☁️</div>
            <h4 className="font-bold text-slate-900 text-sm">Fully Cloud-Based</h4>
            <p className="text-xs text-slate-600">No servers, no hardware, no server room</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Upfront Cost</h4>
            <p className="text-xs text-slate-600">Free to start, affordable to grow</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Instant Scaling</h4>
            <p className="text-xs text-slate-600">Add capacity without hardware</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🛡️</div>
            <h4 className="font-bold text-slate-900 text-sm">Enterprise Security</h4>
            <p className="text-xs text-slate-600">Better than most server rooms</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Accessible Anywhere</h4>
            <p className="text-xs text-slate-600">Not locked in a server room</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Always Current</h4>
            <p className="text-xs text-slate-600">Never obsolete, always updated</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Ditch Your Server Room. Move to the Cloud.</h3>
        <p className="text-indigo-800 leading-relaxed">
          The server room was once essential. It kept your institution's data safe and applications running. But times have changed. Hardware costs too much. Maintenance is a burden. Security is a challenge. And the cloud offers everything your server room does—better, cheaper, and simpler.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is a 100% cloud-based ERP. No servers. No hardware. No server room. Just a simple, affordable subscription that gives your institution everything it needs to run smoothly.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for cloud ERP.</strong> Ditch your server room. Run your institution from the cloud.
        </p>
      </div>
    </>
  );
};

export default ServerRoomObsolete;